import React from "react";
import { InlineMath } from "react-katex";
import { makeClosedTask, makeOptions, normalizeMay2026Variant, pick } from "../../../../matura/MaturaMay2026ClosedTaskSupport";

function primeFactorization(value) {
  const factors = {};
  let rest = value;

  for (let factor = 2; factor * factor <= rest; factor += 1) {
    while (rest % factor === 0) {
      factors[factor] = (factors[factor] || 0) + 1;
      rest /= factor;
    }
  }
  if (rest > 1) factors[rest] = (factors[rest] || 0) + 1;
  return factors;
}

function factorsLatex(factors) {
  return Object.entries(factors)
    .map(([factor, exponent]) => exponent === 1 ? factor : `${factor}^{${exponent}}`)
    .join("\\cdot ");
}

export function task05(variant) {
  const original = {
    baseA: 4, exponentA: 12, baseB: 5, exponentB: 24, divisor: 20, claimedDigits: 25,
  };
  const pool = [
    { baseA: 4, exponentA: 8, baseB: 5, exponentB: 16, divisor: 100, digitOffset: 0 },
    { baseA: 4, exponentA: 7, baseB: 5, exponentB: 14, divisor: 50, digitOffset: -1 },
    { baseA: 2, exponentA: 18, baseB: 5, exponentB: 18, divisor: 3, digitOffset: 0 },
    { baseA: 2, exponentA: 14, baseB: 5, exponentB: 14, divisor: 3, digitOffset: -1 },
    { baseA: 8, exponentA: 5, baseB: 5, exponentB: 15, divisor: 40, digitOffset: 0 },
    { baseA: 8, exponentA: 4, baseB: 5, exponentB: 12, divisor: 10, digitOffset: -1 },
    { baseA: 4, exponentA: 9, baseB: 5, exponentB: 18, divisor: 25, digitOffset: 0 },
    { baseA: 2, exponentA: 16, baseB: 5, exponentB: 16, divisor: 9, digitOffset: -1 },
    { baseA: 2, exponentA: 12, baseB: 5, exponentB: 12, divisor: 20, digitOffset: 0 },
  ];
  const item = variant === "original" ? original : pick(pool);
  const baseATwoPower = { 2: 1, 4: 2, 8: 3 }[item.baseA];
  const tenExponent = baseATwoPower * item.exponentA;
  const actualDigits = tenExponent + 1;
  const claimedDigits = item.claimedDigits ?? actualDigits + item.digitOffset;
  const digitClaim = actualDigits === claimedDigits;
  const baseRewrite = item.baseA === 2
    ? "2^{" + item.exponentA + "}"
    : "(2^{" + baseATwoPower + "})^{" + item.exponentA + "}";
  const numberFactors = { 2: tenExponent, 5: item.exponentB };
  const divisorFactors = primeFactorization(item.divisor);
  const missingFactors = Object.entries(divisorFactors)
    .filter(([factor, exponent]) => (numberFactors[factor] || 0) < exponent);
  const divisible = missingFactors.length === 0;
  const powerFactorization = item.baseA === 2
    ? "N=2^{" + item.exponentA + "}\\cdot 5^{" + item.exponentB + "}"
    : "N=" + item.baseA + "^{" + item.exponentA + "}\\cdot 5^{" + item.exponentB + "}="
      + baseRewrite + "\\cdot 5^{" + item.exponentB + "}=2^{" + tenExponent + "}\\cdot 5^{" + item.exponentB + "}";
  const missingFactorText = missingFactors.length
    ? missingFactors.map(([factor, exponent]) => exponent === 1
      ? `\\text{czynnika }${factor}`
      : `\\text{wymaganych }${exponent}\\text{ czynników }${factor}`).join("\\text{ oraz }")
    : "";
  const divisibilityLatex = item.divisor + "=" + factorsLatex(divisorFactors)
    + ",\\qquad N=2^{" + tenExponent + "}\\cdot 5^{" + item.exponentB + "}"
    + (divisible
      ? "\\text{ zawiera wszystkie wymagane czynniki, więc }" + item.divisor + "\\mid N"
      : "\\text{ nie zawiera }" + missingFactorText + ",\\text{ więc }" + item.divisor + "\\nmid N");
  const digitsLatex = "N=2^{" + tenExponent + "}\\cdot 5^{" + item.exponentB + "}=(2\\cdot 5)^{" + tenExponent
    + "}=10^{" + tenExponent + "},\\qquad 10^{" + tenExponent + "}\\text{ to }1\\text{ i }" + tenExponent
    + "\\text{ zer, czyli liczba ma }" + actualDigits + "\\text{ cyfr}";
  const answer = (divisible ? "P" : "F") + ", " + (digitClaim ? "P" : "F");
  const fixed = [["A", "P, P"], ["B", "P, F"], ["C", "F, P"], ["D", "F, F"]];
  const { options, correctIndex } = makeOptions(answer, [], fixed);
  return makeClosedTask({
    type: "MaturaPowerDivisibilityTruthClosedTask",
    taskNumber: "5",
    variant,
    tags: ["potegi", "notacja-wykladnicza", "podzielnosc"],
    data: {
      answerMode: "truth",
      statementTruth: [divisible, digitClaim],
      divisor: item.divisor,
      instruction: "Oceń prawdziwość poniższych stwierdzeń. Wybierz odpowiedź zgodną z kolejnością zdań.",
      prompt: "Dla liczby naturalnej zapisanej jako:",
      latex: item.baseA + "^{" + item.exponentA + "}\\cdot " + item.baseB + "^{" + item.exponentB + "}",
      afterLatex: (
        <div className="matura-truth-statement-list">
          <div className="matura-truth-statement"><span className="matura-truth-statement__number"><InlineMath math="1." /></span><span>Liczba jest podzielna przez <InlineMath math={String(item.divisor)} />.</span></div>
          <div className="matura-truth-statement"><span className="matura-truth-statement__number"><InlineMath math="2." /></span><span>W zapisie dziesiętnym jest liczbą <InlineMath math={String(claimedDigits)} />-cyfrową.</span></div>
        </div>
      ),
      options,
      correctIndex,
      explanationTitles: [
        "Rozkładamy liczbę na czynniki pierwsze.",
        "Porównujemy rozkład dzielnika z rozkładem liczby.",
        <>Łączymy czynniki <InlineMath math="2" /> i <InlineMath math="5" /> w potęgę liczby <InlineMath math="10" /> i odczytujemy liczbę cyfr.</>,
      ],
      explanationLines: [powerFactorization, divisibilityLatex, digitsLatex],
    },
  });
}

export function generateTask(options = {}) {
  return task05(normalizeMay2026Variant(options));
}

export { checkClosedAnswer as checkAnswer, renderClosedAnswer as renderAnswer, renderClosedExplanation as renderExplanation, renderClosedInput as renderInput, renderClosedTask as renderTask, renderClosedUserAnswer as renderUserAnswer } from "../../../../matura/MaturaMay2026ClosedTaskSupport";
